import { mdiTrayArrowDown } from '@mdi/js';
import Icon from '@mdi/react';
import { CircularProgress, Tooltip } from '@mui/material';
import { observer } from 'mobx-react';
import React from 'react';
import { TooltipTransitionComponent } from '../../../base';
import { CoreCsvExportStore, csvExportStore as defaultCsvExportStore } from '../../../../stores/csv-export';
import { CsvBlobPartsBuilder, downloadCsvBlobParts } from './csv-utils';
import * as S from './table-export.styles';

export interface ExportAllFetchOptions<T = any> {
    onProgress: (loaded: number, total: number) => void;
    signal?: AbortSignal;
    /**
     * Called in page order. Convert items to CSV immediately and do not retain
     * the message objects after this returns.
     */
    onPage: (items: T[], info: { page: number; total: number; loaded: number }) => void | Promise<void>;
}

export interface ExportAllButtonProps<T = any> {
    /** Expected total rows (for progress display). */
    total: number;
    /** Stream all pages via onPage; must not accumulate the full dataset. */
    onFetchAll: (options: ExportAllFetchOptions<T>) => Promise<void>;
    /** Map each item to a flat CSV row keyed by header (not retained as an array). */
    toCsvRow: (item: T) => Record<string, string | number | boolean | null | undefined>;
    /** Column headers in export order. */
    headers: string[];
    filename?: string;
    disabled?: boolean;
    /** Optional toast/notify callback for errors. */
    onError?: (message: string) => void;
    /** Defaults to the shared app-shell singleton. */
    csvExportStore?: CoreCsvExportStore;
    /** Title shown on the floating progress widget. */
    exportTitle?: string;
}

const ExportAllButtonComponent = <T,>({
    total,
    onFetchAll,
    toCsvRow,
    headers,
    filename = 'export-all',
    disabled,
    onError,
    csvExportStore = defaultCsvExportStore,
    exportTitle = 'Exporting messages',
}: ExportAllButtonProps<T>): React.ReactElement => {
    const isExporting = csvExportStore.isActive;
    const mountedRef = React.useRef(true);

    React.useEffect(() => {
        mountedRef.current = true;
        // Intentionally do NOT abort on unmount — export runs on csvExportStore and
        // CsvExportWidget so users can navigate away while it continues.
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const handleClick = (): void => {
        if (isExporting || disabled) {
            return;
        }

        const csvFilename = filename.endsWith('.csv') ? filename : `${filename}.csv`;

        void csvExportStore
            .start({
                total,
                filename: csvFilename,
                title: exportTitle,
                execute: async ({ signal, onProgress }) => {
                    const csvBuilder = new CsvBlobPartsBuilder(headers);

                    await onFetchAll({
                        onProgress,
                        signal,
                        onPage: items => {
                            for (const item of items) {
                                csvBuilder.appendRow(toCsvRow(item));
                            }
                        },
                    });

                    if (signal.aborted) {
                        return;
                    }

                    const parts = csvBuilder.finish();
                    downloadCsvBlobParts(parts, csvFilename);
                },
            })
            .catch((err: any) => {
                // Avoid calling page-level onError after this button unmounted.
                if (!mountedRef.current) {
                    return;
                }
                if (csvExportStore.status === 'failed') {
                    onError?.(err?.message || 'Failed to export all messages');
                } else if (err?.message === 'An export is already in progress') {
                    onError?.(err.message);
                }
            });
    };

    return (
        <S.Container>
            <Tooltip
                placement="top-start"
                title={
                    isExporting
                        ? 'Export already in progress — see the progress panel'
                        : 'Export ALL messages (inbox and done), ignoring table filters. This may take a while. You can navigate away while it runs.'
                }
                TransitionComponent={TooltipTransitionComponent}>
                <div>
                    <S.ActionButton
                        variant="outlined"
                        size="small"
                        type="button"
                        disabled={disabled || isExporting}
                        onClick={handleClick}
                        aria-label="Export all">
                        {isExporting ? (
                            <CircularProgress size={14} thickness={5} />
                        ) : (
                            <>
                                <Icon path={mdiTrayArrowDown} size={0.7} />
                                <S.ExportAllLabel>All</S.ExportAllLabel>
                            </>
                        )}
                    </S.ActionButton>
                </div>
            </Tooltip>
        </S.Container>
    );
};

// observer + generic component: cast to keep JSX generic usable
export const ExportAllButton = observer(ExportAllButtonComponent) as typeof ExportAllButtonComponent;
